import { useCallback, useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { EanInput } from "@/components/barcode/EanInput";
import { api } from "@/lib/apiClient";
import { formatGrossFromNet, parseAmount } from "@/lib/money";
import type { Article } from "@/types/camping";

const EMPTY_INTAKE = {
  quantity: "",
  batch_number: "",
  expires_at: "",
  purchase_price: "",
};

export interface QuickSalePurchaseModalProps {
  open: boolean;
  onClose: () => void;
  onBooked?: (article: Article) => void;
}

export function QuickSalePurchaseModal({
  open,
  onClose,
  onBooked,
}: QuickSalePurchaseModalProps) {
  const [barcode, setBarcode] = useState("");
  const [article, setArticle] = useState<Article | null>(null);
  const [intake, setIntake] = useState(EMPTY_INTAKE);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setBarcode("");
    setArticle(null);
    setIntake(EMPTY_INTAKE);
    setError(null);
    setLookupBusy(false);
    setSubmitting(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
  }, [open, reset]);

  const loadArticle = useCallback(async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError("Bitte Barcode eingeben oder scannen.");
      return;
    }
    setLookupBusy(true);
    setError(null);
    setArticle(null);
    try {
      const found = await api.get<Article>(
        `/api/v1/articles/by-barcode/${encodeURIComponent(trimmed)}`,
      );
      setArticle(found);
      setIntake({
        quantity: "",
        batch_number: "",
        expires_at: "",
        purchase_price: found.purchase_price ?? "",
      });
    } catch {
      setError("Kein Artikel mit diesem Barcode gefunden.");
    } finally {
      setLookupBusy(false);
    }
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!article) return;

    const qty = parseAmount(intake.quantity);
    if (qty == null || qty <= 0) {
      setError("Menge muss größer als 0 sein.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const purchaseNet = parseAmount(intake.purchase_price);
      await api.post(`/api/v1/articles/${article.id}/batches`, {
        batch_number: intake.batch_number.trim() || null,
        quantity: qty,
        purchase_price: purchaseNet,
        expires_at: intake.expires_at || null,
      });

      const refreshed = await api.get<Article>(`/api/v1/articles/by-barcode/${encodeURIComponent(article.barcode ?? barcode.trim())}`);
      onBooked?.(refreshed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Einkauf konnte nicht gebucht werden.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Einkauf / Wareneingang"
      description="Artikel scannen und Bestand mit Charge oder MHD erfassen"
      size="lg"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-5">
        <div className="space-y-2">
          <span className="text-sm font-medium text-label">1. Artikel</span>
          <EanInput
            value={barcode}
            onChange={setBarcode}
            disabled={lookupBusy || submitting}
            onEnter={(code) => void loadArticle(code)}
            cameraOnDesktop
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              loading={lookupBusy}
              disabled={submitting}
              onClick={() => void loadArticle(barcode)}
            >
              Artikel laden
            </Button>
            {article ? (
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={() => {
                  setArticle(null);
                  setIntake(EMPTY_INTAKE);
                  setError(null);
                }}
              >
                Anderen Artikel
              </Button>
            ) : null}
          </div>
        </div>

        {article ? (
          <fieldset className="space-y-3 rounded-lg border border-separator bg-fill-tertiary/50 p-4">
            <legend className="px-1 text-sm font-semibold text-label">
              2. Einkauf erfassen
            </legend>
            <p className="text-sm">
              <span className="font-medium">{article.name}</span>
              <span className="block font-mono text-xs text-label-secondary">
                {article.article_number}
                {article.barcode ? ` · ${article.barcode}` : ""}
              </span>
              <span className="mt-1 block text-xs text-label-secondary">
                Aktueller Bestand: {article.stock_quantity}
                {article.track_inventory ? " (Chargenführung)" : ""}
                {article.current_price != null
                  ? ` · VK ${formatGrossFromNet(article.current_price, article.tax_rate_percent)} brutto`
                  : ""}
              </span>
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm sm:col-span-2">
                Menge *
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  required
                  autoFocus
                  className="eh-input"
                  value={intake.quantity}
                  onChange={(e) =>
                    setIntake((f) => ({ ...f, quantity: e.target.value }))
                  }
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                Chargen-Nr. (optional)
                <input
                  className="eh-input"
                  value={intake.batch_number}
                  onChange={(e) =>
                    setIntake((f) => ({ ...f, batch_number: e.target.value }))
                  }
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                MHD / Ablaufdatum
                <input
                  type="date"
                  className="eh-input"
                  value={intake.expires_at}
                  onChange={(e) =>
                    setIntake((f) => ({ ...f, expires_at: e.target.value }))
                  }
                />
              </label>
              <label className="flex flex-col gap-1 text-sm sm:col-span-2">
                EK netto (optional)
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="eh-input sm:max-w-xs"
                  placeholder="Einkaufspreis dieser Lieferung"
                  value={intake.purchase_price}
                  onChange={(e) =>
                    setIntake((f) => ({ ...f, purchase_price: e.target.value }))
                  }
                />
              </label>
            </div>
            <p className="text-xs text-label-secondary">
              Der Bestand wird als neue Charge gebucht. Ohne MHD und Chargen-Nr. wird
              nur die Menge erfasst.
            </p>
          </fieldset>
        ) : null}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Abbrechen
          </Button>
          <Button type="submit" loading={submitting} disabled={!article}>
            Einkauf buchen
          </Button>
        </div>
      </form>
    </Modal>
  );
}
